import { zodResolver } from '@hookform/resolvers/zod';
import moment from 'moment';
import type { FC } from 'react';
import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import Swal from 'sweetalert2';

import MainButton from '@/components/UI/mobile/MainButton';
import { getUploadFilePath } from '@/helpers/getUploadFilePath';
import { getAxiosWithAuth } from '@/lib/axios';

import AddFixFormItem from '../AddFixFormItem';
import styles from './index.module.scss';
import type { FixForm } from './types';
import { fixFormSchema } from './types';

const AddFixForm: FC<{
  houseNumber: string;
  onClose: () => void;
  fixTypeOptions: string[];
  deviceOptions: { label: string; value: string }[];
}> = ({ houseNumber, onClose, fixTypeOptions, deviceOptions }) => {
  const {
    register,
    unregister,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useForm<FixForm>({
    defaultValues: { houseNumber },
    resolver: zodResolver(fixFormSchema),
  });

  const content = watch('content');
  const categoryName = watch('category.name');

  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const { ref: fileRegisterRef, ...restFileRegister } = register('files');
  const image = watch('files')?.[0];
  const imageUrl = image && URL.createObjectURL(image);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      const newFix: Record<string, unknown> = {
        category: data.category,
        householdNumber: data.houseNumber,
        title: data.title,
        content: data.content,
        bookingTime: moment(data.bookingTime).toISOString(),
        image: null,
      };
      if (data.files?.[0]) {
        const path = await getUploadFilePath(data.files[0], 'fix');
        newFix.image = { name: data.files[0].name, path };
      }
      const axios = getAxiosWithAuth();
      await axios.post('/ezcon/api/fixes', newFix);
      handleClose();
    } catch (error) {
      console.error(error);
      Swal.fire({
        title: '發生錯誤',
        text: '請稍後再試',
        confirmButtonColor: '#69b49e',
        icon: 'error',
      });
    }
  });

  useEffect(() => {
    if (categoryName !== '公共設備') {
      unregister('category.communityDeviceId');
      setValue('category.communityDeviceId', undefined);
    }
    setValue('category.communityDeviceId', deviceOptions?.[0]?.value);
  }, [deviceOptions, setValue, categoryName, unregister]);

  return (
    <form className={styles.createFeedbackForm} onSubmit={onSubmit}>
      <AddFixFormItem label="戶號">
        <input
          type="text"
          className={styles.houseNumber}
          {...register('houseNumber')}
          disabled
        />
      </AddFixFormItem>
      <AddFixFormItem
        label="類別"
        labelFor="type"
        error={errors.category?.name?.message}
        required
      >
        <div className={styles.typeWrapper}>
          <select
            className={`${styles.type} ${
              errors.category?.name ? styles.alert : ''
            }`}
            {...register('category.name', { required: true })}
          >
            {fixTypeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </AddFixFormItem>
      {categoryName === '公共設備' && (
        <AddFixFormItem
          label="設備名稱"
          labelFor="communityDeviceId"
          error={errors.category?.communityDeviceId?.message}
          required
        >
          <div className={styles.typeWrapper}>
            <select
              className={`${styles.type} ${
                errors.category?.communityDeviceId ? styles.alert : ''
              }`}
              {...register('category.communityDeviceId', { required: true })}
            >
              {deviceOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </AddFixFormItem>
      )}
      <AddFixFormItem
        label="標題"
        labelFor="title"
        error={errors.title?.message}
        required
      >
        <input
          type="text"
          {...register('title', { required: true })}
          className={`${styles.title} ${errors.title ? styles.alert : ''}`}
        />
      </AddFixFormItem>
      <AddFixFormItem
        label="內容"
        labelFor="description"
        error={errors.content?.message}
        required
      >
        <div className={styles.descriptionWrapper}>
          <textarea
            className={`${styles.description} ${
              errors.content ? styles.alert : ''
            }`}
            {...register('content', { required: true, maxLength: 100 })}
          />
          <span className={styles.descriptionLength}>
            {content?.length ?? 0}/100
          </span>
        </div>
      </AddFixFormItem>
      <AddFixFormItem
        label="希望處理日期"
        required
        error={errors.bookingTime?.message}
      >
        <input
          type="datetime-local"
          className={`${styles.bookingTime} ${
            errors.bookingTime ? styles.alert : ''
          }`}
          {...register('bookingTime', { required: true })}
        />
      </AddFixFormItem>
      <AddFixFormItem label="附件">
        <input
          id="file"
          className={styles.file}
          type="file"
          accept="image/*"
          {...restFileRegister}
          multiple={false}
          hidden
          ref={(e) => {
            fileRegisterRef(e);
            uploadInputRef.current = e;
          }}
        />
        <button
          type="button"
          className={styles.uploadButton}
          onClick={() => uploadInputRef.current?.click()}
        >
          {imageUrl && <img src={imageUrl} alt="upload image" />}
          <span>上傳檔案</span>
        </button>
      </AddFixFormItem>
      <div className={styles.buttonBox}>
        <MainButton onClick={handleClose}>取消</MainButton>
        <MainButton type="submit">送出</MainButton>
      </div>
    </form>
  );
};

export default AddFixForm;
